import { useCallback, useRef } from 'react';
import type { ReactNode } from 'react';
import type { Project } from '../../../lib/api';
import { calcMvpPercentFromListItem } from '../../../lib/mvpWizard';
import { ProjectProgressBar } from './ProjectProgressBar';
import {
  IconBolt,
  IconCheck,
  IconPen,
  IconTrash,
  IconEye,
  IconHeart,
  HourglassIcon,
  WarnIcon,
} from '../../../components/icons';
import { haptic } from '../../../utils/haptic';
import './ProjectCard.css';

function formatUpdatedAt(updated_at: string): string {
  try {
    const d = new Date(updated_at);
    return d.toLocaleDateString('ru-RU', { day: 'numeric', month: 'short', year: 'numeric' });
  } catch {
    return '';
  }
}

/** Human-readable missing fields for draft hint */
function missingFieldsHint(missing_fields: string[]): string {
  if (!missing_fields?.length) return '';
  const labels: Record<string, string> = {
    project_title: 'название',
    category: 'категорию',
    niche: 'нишу',
    problem: 'проблему',
    what_done: 'решение',
    audience_type: 'аудиторию',
    project_status: 'стадию проекта',
    dev_time: 'срок разработки',
    tools: 'инструменты',
    statuses: 'статусы',
    price: 'цену',
  };
  const first = missing_fields.slice(0, 2).map((f) => labels[f] || f).join(', ');
  return first + (missing_fields.length > 2 ? '…' : '');
}

export interface MyProjectCardProps {
  project: Project;
  onOpen: (id: string) => void;
  onEdit: (id: string) => void;
  onSubmit: (id: string) => void;
  onDelete: (id: string) => void;
  onWithdraw: (id: string) => void;
}

export function MyProjectCard({ project, onOpen, onEdit, onSubmit, onDelete, onWithdraw }: MyProjectCardProps) {
  const lastFireRef = useRef(0);
  const touchRef = useRef<{ x: number; y: number; moved: boolean } | null>(null);

  const fireOpen = useCallback(() => {
    const now = Date.now();
    if (now - lastFireRef.current < 350) return;
    lastFireRef.current = now;
    onOpen(project.id);
  }, [onOpen, project.id]);

  const isPublished = project.status === 'published';
  const isModeration =
    project.status === 'submitted' || project.status === 'needs_fix' || project.status === 'approved';
  const isDraft = project.status === 'draft' || project.status === 'rejected';

  const stripStatus = isPublished ? 'published' : isModeration ? 'moderation' : 'draft';
  const statusPillClass =
    isPublished ? 'published' : isModeration ? 'moderation' : project.status === 'rejected' ? 'rejected' : 'draft';

  let pillText: ReactNode;
  if (isPublished) {
    pillText = <><IconCheck size={10} /> Опубликован</>;
  } else if (isModeration) {
    pillText = <><HourglassIcon size={10} /> Модерация</>;
  } else if (project.status === 'rejected') {
    pillText = <><WarnIcon size={10} color="#f87171" /> Отклонён</>;
  } else {
    pillText = 'Черновик';
  }

  const percent = calcMvpPercentFromListItem(project);
  const updatedStr = formatUpdatedAt(project.updated_at);
  const viewsCount = (project as Project & { views_count?: number }).views_count ?? 0;
  const reactionsCount = (project as Project & { reactions_count?: number }).reactions_count ?? 0;

  return (
    <div
      role="button"
      tabIndex={0}
      className="project-card my-projects-card"
      onClick={fireOpen}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          fireOpen();
        }
      }}
      onTouchStart={(e) => {
        const t = e.changedTouches?.[0];
        if (t) touchRef.current = { x: t.clientX, y: t.clientY, moved: false };
      }}
      onTouchMove={(e) => {
        const s = touchRef.current;
        const t = e.changedTouches?.[0];
        if (s && t && (Math.abs(t.clientX - s.x) > 12 || Math.abs(t.clientY - s.y) > 12)) s.moved = true;
      }}
      onTouchEnd={() => {
        if (!touchRef.current?.moved) fireOpen();
        touchRef.current = null;
      }}
    >
      <div className={`project-card-strip ${stripStatus}`} />
      <div className="project-card-body">
        <div className="project-card-top">
          <div className="project-card-icon-wrap">
            <IconBolt size={20} />
          </div>
          <div className="project-card-top-content">
            <h3 className="project-card-title">{project.title_short || 'Без названия'}</h3>
            <p className="project-card-meta">{updatedStr}</p>
          </div>
          <span className={`project-card-status ${statusPillClass}`}>
            {pillText}
          </span>
        </div>

        {isPublished && (
          <>
            <div className="project-card-metrics">
              <span className="project-card-metric"><IconEye size={12} /> {viewsCount}</span>
              <span className="project-card-metric"><IconHeart size={12} /> {reactionsCount}</span>
            </div>
            <div
              className="project-card-actions"
              onClick={(e) => e.stopPropagation()}
              onPointerDown={(e) => e.stopPropagation()}
              onTouchEnd={(e) => e.stopPropagation()}
            >
              <button
                type="button"
                className="project-card-action-edit"
                onClick={(e) => {
                  e.stopPropagation();
                  haptic.tap();
                  onWithdraw(project.id);
                }}
              >
                <IconPen size={12} /> Вернуть в черновик
              </button>
              <button
                type="button"
                className="project-card-action-edit"
                onClick={(e) => {
                  e.stopPropagation();
                  haptic.tap();
                  onEdit(project.id);
                }}
              >
                <IconPen size={12} /> Редактировать
              </button>
              {project.can_delete && (
                <button
                  type="button"
                  className="project-card-action-delete"
                  onClick={(e) => {
                    e.stopPropagation();
                    haptic.warning();
                    if (window.confirm('Удалить проект? Это действие нельзя отменить.')) onDelete(project.id);
                  }}
                >
                  <IconTrash size={14} />
                </button>
              )}
            </div>
          </>
        )}

      {isModeration && (
        <>
          <ProjectProgressBar percent={100} variant="moderation" />
          <div
            style={{
              fontSize: 11,
              color: 'var(--color-warning, #f59e0b)',
              marginBottom: 8,
            }}
          >
            Проверяем твой проект, обычно занимает до 2 часов
          </div>
          <div
            className="project-card-actions"
            onClick={(e) => e.stopPropagation()}
            onPointerDown={(e) => e.stopPropagation()}
            onTouchEnd={(e) => e.stopPropagation()}
            >
            <button
              type="button"
              className="project-card-action-edit"
              onClick={(e) => {
                e.stopPropagation();
                haptic.tap();
                onWithdraw(project.id);
              }}
            >
              <IconPen size={12} /> Вернуть в черновик
            </button>
            <button
              type="button"
              className="project-card-action-edit"
              onClick={(e) => {
                e.stopPropagation();
                haptic.tap();
                onEdit(project.id);
              }}
            >
              <IconPen size={12} /> Редактировать
            </button>
            {project.can_delete && (
              <button
                type="button"
                className="project-card-action-delete"
                onClick={(e) => {
                  e.stopPropagation();
                  haptic.warning();
                  if (window.confirm('Удалить проект? Это действие нельзя отменить.')) onDelete(project.id);
                }}
              >
                <IconTrash size={14} />
              </button>
            )}
          </div>
        </>
      )}

      {isDraft && (
        <>
          <ProjectProgressBar percent={percent} variant="draft" />
          <div style={{ fontSize: 11, color: 'var(--app-text-hint)', marginBottom: 4 }}>
            Заполнено {percent}%
            {missingFieldsHint(project.missing_fields ?? []) && ` — осталось добавить ${missingFieldsHint(project.missing_fields ?? [])}`}
          </div>
          {project.status === 'rejected' && project.rejected_reason && (
            <div style={{ fontSize: 11, color: '#f87171', marginBottom: 8 }}>
              {project.rejected_reason}
            </div>
          )}
          <div
            className="project-card-actions"
            onClick={(e) => e.stopPropagation()}
            onPointerDown={(e) => e.stopPropagation()}
            onTouchEnd={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              className="project-card-action-edit"
              style={{
                background: 'rgba(124, 58, 237, 0.12)',
                borderColor: 'rgba(124, 58, 237, 0.25)',
                color: 'var(--brand-purple-light)',
              }}
              onClick={(e) => {
                e.stopPropagation();
                haptic.tap();
                onSubmit(project.id);
              }}
              disabled={!project.can_submit}
            >
              <IconBolt size={12} /> На модерацию
            </button>
            <button
              type="button"
              className="project-card-action-edit"
              onClick={(e) => {
                e.stopPropagation();
                haptic.tap();
                onEdit(project.id);
              }}
            >
              <IconPen size={12} /> Редактировать
            </button>
            <button
              type="button"
              className="project-card-action-delete"
              onClick={(e) => {
                e.stopPropagation();
                haptic.warning();
                if (window.confirm('Удалить проект? Это действие нельзя отменить.')) onDelete(project.id);
              }}
            >
              <IconTrash size={14} />
            </button>
          </div>
        </>
      )}
      </div>
    </div>
  );
}
